// src/pages/GroupView.jsx
import React, { useEffect, useState, useCallback } from 'react';
import { useRefresh } from '../contexts/RefreshContext';
import {
  Box,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Badge,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  IconButton,
  Tooltip,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Users,
  Clock3,
  CheckCircle2,
  MapPin,
  Edit3,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Calendar,
  Palette,
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

// ── Helpers ────────────────────────────────────────────────────────────────

function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function fromISO(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function diffDays(startStr, endStr) {
  const s = fromISO(startStr);
  const e = fromISO(endStr);
  return Math.round((e - s) / 86400000);
}

function todayISO() {
  return toISO(new Date());
}

function isSingleDay(startDate, endDate) {
  return startDate === endDate;
}

function dayLabel(dateStr) {
  const date = fromISO(dateStr);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'long' });
  const formatted = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${weekday}, ${formatted}`;
}

function shortDateLabel(dateStr) {
  return fromISO(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function tableDateLabel(dateStr) {
  const date = fromISO(dateStr);
  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const m = date.getMonth() + 1;
  const d = date.getDate();
  return `${weekday} ${m}/${d}`;
}

function periodLabel(startDate, endDate) {
  if (isSingleDay(startDate, endDate)) return dayLabel(startDate);
  return `${shortDateLabel(startDate)} – ${shortDateLabel(endDate)}`;
}

const progressColor = (p) => (p >= 100 ? 'success' : p >= 80 ? 'info' : p >= 60 ? 'warning' : 'error');
const progressLabel = (p) => (p >= 100 ? 'Complete' : p >= 80 ? 'Excellent' : p >= 60 ? 'Good' : 'Behind');
const progressHex   = (p) => (p >= 100 ? '#2e7d32' : p >= 80 ? '#0d47a1' : p >= 60 ? '#e65100' : '#b71c1c');
const progressGrad  = (p) =>
  p >= 100 ? 'linear-gradient(90deg, #2e7d32, #66bb6a)'
  : p >= 80 ? 'linear-gradient(90deg, #0d47a1, #42a5f5)'
  : p >= 60 ? 'linear-gradient(90deg, #e65100, #ffa726)'
  : 'linear-gradient(90deg, #b71c1c, #ef5350)';

const HEADER_COLOR_PRESETS = [
  { label: 'Pink',   value: 'linear-gradient(135deg, #920444 0%, #fc2aaf 100%)' },
  { label: 'Purple', value: 'linear-gradient(135deg, #4a0080 0%, #9c27b0 100%)' },
  { label: 'Blue',   value: 'linear-gradient(135deg, #0d47a1 0%, #42a5f5 100%)' },
  { label: 'Teal',   value: 'linear-gradient(135deg, #00695c 0%, #26c6da 100%)' },
  { label: 'Green',  value: 'linear-gradient(135deg, #1b5e20 0%, #66bb6a 100%)' },
  { label: 'Orange', value: 'linear-gradient(135deg, #bf360c 0%, #ff7043 100%)' },
  { label: 'Dark',   value: 'linear-gradient(135deg, #212121 0%, #546e7a 100%)' },
  { label: 'Gold',   value: 'linear-gradient(135deg, #7b5e00 0%, #f9a825 100%)' },
];

// ── Checklist Modal ────────────────────────────────────────────────────────

function ChecklistModal({ open, onClose, employeeName, checklistData, initialTab }) {
  const [activeTab, setActiveTab] = useState(initialTab || 'opening');

  useEffect(() => {
    if (open) setActiveTab(initialTab || 'opening');
  }, [open, initialTab]);

  if (!checklistData) return null;

  const opening = checklistData.opening || {};
  const closing = checklistData.closing || {};
  const report  = checklistData.report  || {};

  const tabs = [
    { key: 'opening', emoji: '📋', label: 'Opening', data: opening },
    { key: 'closing', emoji: '🌙', label: 'Closing', data: closing },
    { key: 'report',  emoji: '📄', label: 'Report',  data: report  },
  ];

  const getTabBadge = (tab) => {
    const d = tab.data || {};
    if (tab.key === 'report') {
      return !d.submitted || !d.is_complete ? '!' : null;
    }
    if (!d.submitted) return '!';
    return d.unchecked_count > 0 ? d.unchecked_count : null;
  };

  const renderItems = (items = []) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      {items.map((it) => (
        <Box
          key={it.field}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.6, borderRadius: 1.5,
            bgcolor: it.checked ? 'rgba(46,125,50,0.05)' : 'rgba(211,47,47,0.06)',
            border: '1px solid',
            borderColor: it.checked ? 'rgba(46,125,50,0.15)' : 'rgba(211,47,47,0.2)',
          }}
        >
          <Typography sx={{ fontSize: '0.9rem', lineHeight: 1, flexShrink: 0 }}>
            {it.checked ? '✅' : '❌'}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: it.checked ? 400 : 600,
              color: it.checked ? 'text.secondary' : 'error.main',
              fontSize: '0.8rem',
            }}
          >
            {it.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderReport = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {[
        { label: 'Activities Summary', value: report.activities_summary },
        { label: 'Incidents',          value: report.incidents          },
        { label: 'Measures Taken',     value: report.measures_taken     },
        { label: 'General Notes',      value: report.general_notes      },
        { label: 'Damaged Materials',  value: report.damaged_materials  },
      ].map(({ label, value }) => (
        <Box key={label}>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}
          >
            {label}
          </Typography>
          <Box sx={{ mt: 0.4, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: '#f8f8f8', border: '1px solid #ebebeb', minHeight: 34 }}>
            <Typography variant="body2" sx={{ fontSize: '0.8rem', color: value ? 'text.primary' : 'text.disabled', whiteSpace: 'pre-wrap' }}>
              {value || '—'}
            </Typography>
          </Box>
        </Box>
      ))}
      {report.end_time && (
        <Typography variant="caption" color="text.secondary">
          End time: {report.end_time}
        </Typography>
      )}
    </Box>
  );

  const currentTab   = tabs.find((t) => t.key === activeTab);
  const notSubmitted = !currentTab?.data?.submitted;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', pb: 0.5, fontSize: '1rem' }}>
        {employeeName} — Checklists
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Tab bar */}
        <Box sx={{ display: 'flex', borderBottom: '1px solid #ebebeb' }}>
          {tabs.map((t) => {
            const isActive = t.key === activeTab;
            const badge    = getTabBadge(t);
            return (
              <Box
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                sx={{
                  flex: 1, py: 1.25, textAlign: 'center', cursor: 'pointer',
                  borderBottom: isActive ? '2px solid #1976d2' : '2px solid transparent',
                  color: isActive ? '#1976d2' : 'text.secondary',
                  fontWeight: isActive ? 700 : 400,
                  fontSize: '0.78rem',
                  position: 'relative',
                  userSelect: 'none',
                  transition: 'all 0.15s',
                  '&:hover': { bgcolor: '#f5f5f5' },
                }}
              >
                {t.emoji} {t.label}
                {badge != null && (
                  <Box sx={{
                    position: 'absolute', top: 5, right: 'calc(50% - 28px)',
                    minWidth: 15, height: 15, borderRadius: '999px',
                    bgcolor: 'error.main', color: 'white',
                    fontSize: '0.58rem', fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    px: 0.35, lineHeight: 1,
                  }}>
                    {badge}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {notSubmitted ? (
            <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
              Not submitted yet.
            </Alert>
          ) : activeTab === 'report' ? (
            renderReport()
          ) : (
            renderItems(currentTab?.data?.items || [])
          )}

          {/* Notes — opening/closing only */}
          {!notSubmitted && activeTab !== 'report' && currentTab?.data?.notes && (
            <Box>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.62rem', letterSpacing: 0.5 }}
              >
                Notes
              </Typography>
              <Box sx={{ mt: 0.4, px: 1.5, py: 0.75, borderRadius: 1.5, bgcolor: '#f8f8f8', border: '1px solid #ebebeb' }}>
                <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                  {currentTab.data.notes}
                </Typography>
              </Box>
            </Box>
          )}

          {/* Closing: validated tips/sales */}
          {!notSubmitted && activeTab === 'closing' &&
            (closing.validated_tips != null || closing.validated_sales != null) && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              {closing.validated_sales != null && (
                <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#15803d', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase' }}>
                    Sales
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#15803d' }}>
                    ${closing.validated_sales.toFixed(2)}
                  </Typography>
                </Box>
              )}
              {closing.validated_tips != null && (
                <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: '#fffbeb', border: '1px solid #fde68a', textAlign: 'center' }}>
                  <Typography variant="caption" sx={{ color: '#b45309', fontWeight: 700, fontSize: '0.62rem', textTransform: 'uppercase' }}>
                    Tips
                  </Typography>
                  <Typography sx={{ fontWeight: 800, fontSize: '1rem', color: '#b45309' }}>
                    ${closing.validated_tips.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={onClose} variant="outlined" size="small">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Employee Card ──────────────────────────────────────────────────────────

function EmployeeCard({ employee, isSingle, isRangeToday, live, checklistData }) {
  const [expanded, setExpanded] = useState(false);
  const [checklistModal, setChecklistModal] = useState({ open: false, tab: 'opening' });

  const breakdown = employee.daily_breakdown || [];
  const emoji     = employee.avatar_emoji || '👤';
  const isWorking = live?.status === 'WORKING';

  let sales, goal;
  if (isSingle) {
    const dayEntry = breakdown[0] || null;
    sales = dayEntry?.daily_sales ?? employee.current_sales ?? 0;
    goal  = dayEntry?.daily_goal  ?? employee.weekly_goal  ?? 0;
  } else {
    sales = breakdown.reduce((s, d) => s + (d.daily_sales || 0), 0);
    goal  = breakdown.reduce((s, d) => s + (d.daily_goal  || 0), 0);
  }

  const pct = goal > 0 ? (sales / goal) * 100 : 0;

  const clockIn      = live?.clock_in  || (isSingle ? breakdown[0]?.clock_in  : null) || null;
  const clockOut     = live?.clock_out || (isSingle ? breakdown[0]?.clock_out : null) || null;
  const hasClockData = isSingle && clockIn && clockIn !== '--' && clockIn !== '--:--';

  const displayHotel = isSingle
    ? (live?.hotel || breakdown[0]?.clocked_hotel || breakdown[0]?.scheduled_hotel || null)
    : null;

  const activeDays = breakdown.filter((d) => d.clock_in !== '--' || (d.daily_sales || 0) > 0);

  // ── Checklist badges ──────────────────────────────────────────────────────
  const showChecklist = isSingle && checklistData;

  // opening badge só aparece se foi submetido e tem itens faltando
  const openingBadge = showChecklist && checklistData.opening?.submitted
    ? (checklistData.opening.unchecked_count > 0 ? checklistData.opening.unchecked_count : null)
    : null;

  // closing badge só aparece se foi submetido e tem itens faltando
  const closingBadge = showChecklist && checklistData.closing?.submitted
    ? (checklistData.closing.unchecked_count > 0 ? checklistData.closing.unchecked_count : null)
    : null;

  // report nunca tem badge
  const reportBadge = null;

  const checklistBtnSx = (hasBadge) => ({
    position: 'relative',
    width: 24, height: 24,
    borderRadius: '50%',
    fontSize: '0.72rem',
    lineHeight: 1,
    border: hasBadge ? '1.5px solid' : '1px solid #e0e0e0',
    borderColor: hasBadge ? 'rgba(211,47,47,0.5)' : '#e0e0e0',
    bgcolor: hasBadge ? 'rgba(211,47,47,0.06)' : '#fafafa',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
    transition: 'background 0.15s',
    '&:hover': { bgcolor: '#f0f0f0' },
  });

  const badgeDotSx = {
    position: 'absolute', top: -5, right: -5,
    minWidth: 14, height: 14, borderRadius: '999px',
    bgcolor: 'error.main', color: 'white',
    fontSize: '0.52rem', fontWeight: 800,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    px: 0.3, lineHeight: 1,
    pointerEvents: 'none',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  };

  return (
    <Box
      sx={{
        borderRadius: 3,
        border: '1px solid',
        borderColor: isWorking ? 'success.light' : '#e8e8e8',
        bgcolor: 'white',
        boxShadow: isWorking ? '0 2px 12px rgba(76,175,80,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 4px 16px rgba(0,0,0,0.12)' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top color strip */}
      <Box sx={{ height: 5, flexShrink: 0, background: progressGrad(pct) }} />

      <Box sx={{ p: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>

        {/* Identity row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{
            width: 36, height: 36, flexShrink: 0,
            borderRadius: '50%', bgcolor: '#f5f5f5',
            border: '2px solid', borderColor: isWorking ? 'success.main' : '#e0e0e0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '1.1rem', position: 'relative',
          }}>
            {emoji}
            {isWorking && (
              <Box sx={{ position: 'absolute', bottom: 0, right: 0, width: 9, height: 9, borderRadius: '50%', bgcolor: 'success.main', border: '2px solid white' }} />
            )}
          </Box>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '0.82rem' }} noWrap>
              {employee.name}
            </Typography>
            {isSingle && displayHotel && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, mt: 0.15 }}>
                <MapPin size={10} color="#999" style={{ flexShrink: 0 }} />
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayHotel}
                </Typography>
              </Box>
            )}
            {!isSingle && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                {activeDays.length} day{activeDays.length !== 1 ? 's' : ''} worked
              </Typography>
            )}
          </Box>

         {/* Working/Done chip + checklist buttons */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>

            {showChecklist && (
              <>
                <Tooltip title="Opening checklist">
                  <Box sx={checklistBtnSx(openingBadge != null)} onClick={() => setChecklistModal({ open: true, tab: 'opening' })}>
                    📋
                    {openingBadge != null && <Box sx={badgeDotSx}>{openingBadge}</Box>}
                  </Box>
                </Tooltip>
                <Tooltip title="Closing checklist">
                  <Box sx={checklistBtnSx(closingBadge != null)} onClick={() => setChecklistModal({ open: true, tab: 'closing' })}>
                    🌙
                    {closingBadge != null && <Box sx={badgeDotSx}>{closingBadge}</Box>}
                  </Box>
                </Tooltip>
                <Tooltip title="Daily report">
                  <Box sx={checklistBtnSx(reportBadge != null)} onClick={() => setChecklistModal({ open: true, tab: 'report' })}>
                    📄
                    {reportBadge != null && <Box sx={badgeDotSx}>{reportBadge}</Box>}
                  </Box>
                </Tooltip>
              </>
            )}

            {live && (
              <Tooltip title={isWorking ? 'Working' : 'Done'}>
                <Box sx={{
                  width: 24, height: 24,
                  borderRadius: '50%',
                  fontSize: '0.72rem',
                  lineHeight: 1,
                  border: '1px solid',
                  borderColor: isWorking ? 'rgba(76,175,80,0.5)' : 'rgba(46,125,50,0.4)',
                  bgcolor: isWorking ? 'rgba(76,175,80,0.08)' : 'rgba(46,125,50,0.08)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {isWorking ? '🟢' : '✅'}
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>

        {/* Clock row — single day only */}
        {hasClockData && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.3, bgcolor: '#f8f8f8', borderRadius: 1.5 }}>
            <Clock3 size={10} color="#888" />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
              {clockIn} – {clockOut || '--:--'}
            </Typography>
          </Box>
        )}

        {/* Sales / goal */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '0.95rem', color: pct >= 100 ? 'success.main' : 'text.primary' }}>
            ${sales.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
            / ${goal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </Typography>
        </Box>

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={Math.min(pct, 100)}
          color={progressColor(pct)}
          sx={{ height: 5, borderRadius: 4, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { borderRadius: 4 } }}
        />

        {/* Footer */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Chip label={progressLabel(pct)} color={progressColor(pct)} size="small" sx={{ fontSize: 9, height: 16 }} />
          {pct >= 100 ? (
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.3, fontSize: '0.67rem' }}>
              <CheckCircle2 size={10} />
              +{(sales - goal).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.67rem' }}>
              {(goal - sales).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} to go
            </Typography>
          )}
        </Box>

        {/* ── Expand toggle — multi-day only ── */}
        {!isSingle && breakdown.length > 0 && (
          <>
            <Box
              onClick={() => setExpanded((v) => !v)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                mt: 0.25, pt: 0.75,
                borderTop: '1px dashed #e0e0e0',
                cursor: 'pointer',
                color: 'text.disabled',
                '&:hover': { color: 'text.primary' },
                transition: 'color 0.15s',
                userSelect: 'none',
              }}
            >
              <Typography variant="caption" sx={{ fontSize: '0.67rem', fontWeight: 600 }}>
                {expanded ? 'Hide details' : 'Show daily breakdown'}
              </Typography>
              {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </Box>

            <Collapse in={expanded}>
              <Box sx={{ mx: -1.5, px: 1, pb: 0.5 }}>
                <Table size="small" sx={{ '& td, & th': { py: 0.45, px: 0.5, fontSize: '0.67rem', border: 0 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled' }}>Hotel</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled' }}>In</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled' }}>Out</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled', textAlign: 'right' }}>Sales</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: 'text.disabled', textAlign: 'right' }}>Goal</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdown.map((day, idx) => {
                      const ds     = day.daily_sales || 0;
                      const dg     = day.daily_goal  || 0;
                      const dp     = dg > 0 ? (ds / dg) * 100 : 0;
                      const active = day.clock_in !== '--' || ds > 0;
                      const hotel  = day.clocked_hotel || day.scheduled_hotel || day.hotel_name || '—';

                      return (
                        <TableRow key={day.date} sx={{ opacity: active ? 1 : 0.38, bgcolor: idx % 2 === 0 ? '#f9f9f9' : 'transparent' }}>
                          <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{tableDateLabel(day.date)}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', maxWidth: 65, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hotel}</TableCell>
                          <TableCell sx={{ color: day.clock_in !== '--' ? '#333' : '#ccc' }}>{day.clock_in || '--'}</TableCell>
                          <TableCell sx={{ color: day.clock_out && day.clock_out !== '--' ? '#333' : '#ccc' }}>{day.clock_out || '--'}</TableCell>
                          <TableCell sx={{ textAlign: 'right', fontWeight: ds > 0 ? 700 : 400, color: dp >= 100 ? 'success.main' : 'text.primary' }}>
                            {ds > 0 ? `$${ds.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
                          </TableCell>
                          <TableCell sx={{ textAlign: 'right', color: 'text.secondary' }}>
                            {dg > 0 ? `$${dg.toLocaleString('en-US', { maximumFractionDigits: 0 })}` : '—'}
                          </TableCell>
                        </TableRow>
                      );
                    })}

                    {/* Totals row */}
                    <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                      <TableCell colSpan={4} sx={{ fontWeight: 700, fontSize: '0.67rem' }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'right', color: pct >= 100 ? 'success.main' : 'text.primary', fontSize: '0.67rem' }}>
                        ${sales.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, textAlign: 'right', color: 'text.secondary', fontSize: '0.67rem' }}>
                        ${goal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </>
        )}

      </Box>

      {/* Checklist Modal */}
      {showChecklist && (
        <ChecklistModal
          open={checklistModal.open}
          onClose={() => setChecklistModal((s) => ({ ...s, open: false }))}
          employeeName={employee.name}
          checklistData={checklistData}
          initialTab={checklistModal.tab}
        />
      )}
    </Box>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────

const GroupView = () => {
  const { user } = useAuth();
  const { refreshKey } = useRefresh();
  const roleId           = user?.role_id;
  const isSupervisor     = roleId === 4;
  const isManagerOrAdmin = roleId === 2 || roleId === 3;
  const canChangeColor   = isSupervisor || isManagerOrAdmin;

  const [startDate, setStartDate] = useState(() => todayISO());
  const [endDate, setEndDate]     = useState(() => todayISO());

  const [groups, setGroups]                   = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [groupInfo, setGroupInfo]             = useState(null);
  const [goalsData, setGoalsData]             = useState(null);
  const [liveActivity, setLiveActivity]       = useState(null);
  const [checklistData, setChecklistData]     = useState({}); // { [employee_id]: { opening, closing, report } }

  const [headerGradient, setHeaderGradient]   = useState(HEADER_COLOR_PRESETS[0].value);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [savingColor, setSavingColor]         = useState(false);
  const [pendingGradient, setPendingGradient] = useState(HEADER_COLOR_PRESETS[0].value);

  const [loading, setLoading]       = useState(true);
  const [subLoading, setSubLoading] = useState(false);
  const [error, setError]           = useState(null);
  const [snackbar, setSnackbar]     = useState({ open: false, message: '', severity: 'info' });
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming]     = useState(false);

  const showSnack = (message, severity = 'info') =>
    setSnackbar({ open: true, message, severity });

  const fetchHeaderColor = useCallback(async (groupId) => {
    if (!groupId) return;
    try {
      const res = await api.get(`/groups/${groupId}/header-color`);
      if (res.data?.header_color) {
        setHeaderGradient(res.data.header_color);
        setPendingGradient(res.data.header_color);
      }
    } catch { /* silent */ }
  }, []);

  const fetchGroupData = useCallback(async (groupId, start, end, showSpinner = true) => {
    if (!groupId) return;
    if (showSpinner) setSubLoading(true);
    setError(null);

    const today     = todayISO();
    const isLiveDay = start === today && end === today;

    try {
      const promises = [
        api.get(`/groups/${groupId}/employees/weekly-goals-progress`, {
          params: { start_date: start, end_date: end },
        }),
      ];
      if (isLiveDay) promises.push(api.get(`/groups/${groupId}/live-activity`));

      const [goalsRes, liveRes] = await Promise.all(promises);
      setGoalsData(goalsRes.data);
      setLiveActivity(isLiveDay ? (liveRes?.data || null) : null);
      setGroupInfo({
        group_id:      goalsRes.data?.group?.id ?? groupId,
        group_name:    goalsRes.data?.group?.name ?? 'Group',
        members_count: goalsRes.data?.summary?.active_employees ?? 0,
      });

      // Checklist summary — só para hoje
    if (start === end) {
      try {
        const clRes = await api.get(`/groups/${groupId}/checklist-summary`, { params: { date: start } });
          const byEmp = {};
          for (const e of clRes.data?.employees || []) byEmp[e.employee_id] = e;
          setChecklistData(byEmp);
        } catch { /* silent — checklist é opcional */ }
      } else if (start !== end) {
      setChecklistData({});
      }
    } catch (e) {
      setError(e?.response?.data?.msg || 'Error loading group data');
    } finally {
      if (showSpinner) setSubLoading(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        if (isSupervisor) {
          const g = await api.get('/supervisor/group').then((r) => r.data);
          if (!g?.group_id) { setError('You do not have a group assigned yet.'); return; }
          setGroups([{ id: g.group_id, name: g.group_name, members_count: g.members_count ?? 0 }]);
          setGroupInfo(g);
          setSelectedGroupId(g.group_id);
          await Promise.all([fetchGroupData(g.group_id, startDate, endDate, false), fetchHeaderColor(g.group_id)]);
        } else if (isManagerOrAdmin) {
          const list = await api.get('/groups').then((r) => r.data);
          const arr  = Array.isArray(list) ? list : [];
          setGroups(arr);
          const firstId = arr[0]?.id ?? null;
          setSelectedGroupId(firstId);
          if (firstId) await Promise.all([fetchGroupData(firstId, startDate, endDate, false), fetchHeaderColor(firstId)]);
        } else {
          setError('Role not supported on this page.');
        }
      } catch (e) {
        setError(e?.response?.data?.msg || 'Error loading data');
      } finally {
        setLoading(false);
      }
    };
    if (roleId) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  useEffect(() => {
    if (!selectedGroupId) return;
    fetchGroupData(selectedGroupId, startDate, endDate, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  useEffect(() => {
    const today = todayISO();
    if (!selectedGroupId || startDate !== today || endDate !== today) return;
    const id = setInterval(() => fetchGroupData(selectedGroupId, startDate, endDate, false), 300_000);
    return () => clearInterval(id);
  }, [selectedGroupId, startDate, endDate, fetchGroupData]);

  const shiftRange = (delta) => {
    const span     = diffDays(startDate, endDate);
    const newStart = toISO(addDays(fromISO(startDate), delta));
    const newEnd   = toISO(addDays(fromISO(newStart), span));
    setStartDate(newStart);
    setEndDate(newEnd);
    if (selectedGroupId) fetchGroupData(selectedGroupId, newStart, newEnd, true);
  };

  const goToday = () => {
    const today = todayISO();
    setStartDate(today);
    setEndDate(today);
    if (selectedGroupId) fetchGroupData(selectedGroupId, today, today, true);
  };

  const onStartDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const newEnd = val > endDate ? val : endDate;
    setStartDate(val);
    setEndDate(newEnd);
    if (selectedGroupId) fetchGroupData(selectedGroupId, val, newEnd, true);
  };

  const onEndDateChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const newStart = val < startDate ? val : startDate;
    setEndDate(val);
    setStartDate(newStart);
    if (selectedGroupId) fetchGroupData(selectedGroupId, newStart, val, true);
  };

  const onChangeGroup = async (gid) => {
    setSelectedGroupId(gid);
    await Promise.all([fetchGroupData(gid, startDate, endDate, true), fetchHeaderColor(gid)]);
  };

  const openRename = () => { setRenameValue(groupInfo?.group_name || ''); setRenameOpen(true); };

  const submitRename = async () => {
    if (!renameValue.trim()) return;
    const id = groupInfo?.group_id || selectedGroupId;
    if (!id) return;
    setRenaming(true);
    try {
      await api.put(`/groups/${id}`, { name: renameValue.trim() });
      setRenameOpen(false);
      if (isSupervisor) {
        const g = await api.get('/supervisor/group').then((r) => r.data);
        setGroups([{ id: g.group_id, name: g.group_name, members_count: g.members_count ?? 0 }]);
        setGroupInfo(g);
        await fetchGroupData(g.group_id, startDate, endDate, false);
      } else {
        const arr = await api.get('/groups').then((r) => (Array.isArray(r.data) ? r.data : []));
        setGroups(arr);
        await fetchGroupData(selectedGroupId, startDate, endDate, false);
      }
      showSnack('Group renamed successfully!', 'success');
    } catch (e) {
      showSnack(e?.response?.data?.msg || 'Error renaming group', 'error');
    } finally {
      setRenaming(false);
    }
  };

  const openColorPicker   = () => { setPendingGradient(headerGradient); setColorPickerOpen(true); };
  const cancelColorPicker = () => { setPendingGradient(headerGradient); setColorPickerOpen(false); };

  const saveHeaderColor = async () => {
    const id = groupInfo?.group_id || selectedGroupId;
    if (!id) return;
    setSavingColor(true);
    try {
      await api.put(`/groups/${id}/header-color`, { header_color: pendingGradient });
      setHeaderGradient(pendingGradient);
      setColorPickerOpen(false);
      showSnack('Header color saved!', 'success');
    } catch (e) {
      showSnack(e?.response?.data?.msg || 'Error saving color', 'error');
    } finally {
      setSavingColor(false);
    }
  };

  const today           = todayISO();
  const isRangeToday    = startDate === today && endDate === today;
  const isSingle        = isSingleDay(startDate, endDate);
  const groupName       = groupInfo?.group_name || goalsData?.group?.name || 'Group';
  const membersCount    = groupInfo?.members_count ?? goalsData?.summary?.active_employees ?? 0;
  const totalSales      = goalsData?.summary?.total_sales || 0;
  const totalGoals      = goalsData?.summary?.total_goals || 0;
  const overallProgress = totalGoals > 0 ? (totalSales / totalGoals) * 100 : 0;

  const activeEmployees = goalsData?.employees || [];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 900, mx: 'auto' }}>

      {isManagerOrAdmin && groups.length > 1 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ px: 2, py: 1.5, borderRadius: 2, border: '1.5px solid #d0d0d0', bgcolor: 'white', boxShadow: '0 1px 6px rgba(0,0,0,0.07)', display: 'inline-block' }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Select Group</InputLabel>
              <Select value={selectedGroupId || ''} onChange={(e) => onChangeGroup(e.target.value)} label="Select Group">
                {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {subLoading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

        {/* ── Summary header ── */}
        <Box sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', background: headerGradient, color: 'white', p: 2.5, position: 'relative', overflow: 'hidden' }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', pointerEvents: 'none' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.05)', pointerEvents: 'none' }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Badge badgeContent={membersCount} color="error" max={999}><Users size={24} /></Badge>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{groupName}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.85 }}>{membersCount} {membersCount === 1 ? 'member' : 'members'}</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {canChangeColor && (
                <Tooltip title="Change header color">
                  <IconButton size="small" onClick={openColorPicker} sx={{ color: 'white', border: '1px solid rgba(255,255,255,0.4)', borderRadius: 1.5, p: 0.75, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
                    <Palette size={16} />
                  </IconButton>
                </Tooltip>
              )}
              <Button variant="outlined" size="small" startIcon={<Edit3 size={16} />} onClick={openRename}
                sx={{ borderColor: 'rgba(255,255,255,0.5)', color: 'white', textTransform: 'none', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                Rename
              </Button>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>${totalSales.toLocaleString()}</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>/ ${totalGoals.toLocaleString()} goal</Typography>
          </Box>
          <Typography variant="body2" sx={{ fontWeight: overallProgress >= 100 ? 'bold' : 'normal', mb: 1.5, opacity: 0.95 }}>
            {overallProgress.toFixed(1)}% of {isRangeToday ? "today's" : isSingle ? "day's" : "period's"} goal
          </Typography>
          <LinearProgress variant="determinate" value={Math.min(overallProgress, 100)}
            sx={{ height: 10, borderRadius: 5, bgcolor: 'rgba(255,255,255,0.3)', '& .MuiLinearProgress-bar': { borderRadius: 5, bgcolor: overallProgress >= 100 ? '#4caf50' : 'white' } }} />
        </Box>

        {/* ── Date range picker ── */}
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>

          {/* Date inputs row — always visible */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>

            {/* Prev arrow — desktop only */}
            <Tooltip title={isSingle ? 'Previous day' : 'Previous period'}>
              <IconButton size="small" onClick={() => shiftRange(-(isSingle ? 1 : diffDays(startDate, endDate) + 1))}
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.secondary', border: '1.5px solid #d0d0d0', borderRadius: 1.5, p: 0.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <ChevronLeft size={18} />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1.5px solid #d0d0d0', borderRadius: 2, px: 1, py: 0.75, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', minWidth: 130 }}>
              <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25, userSelect: 'none' }}>From</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input type="date" value={startDate} onChange={onStartDateChange}
                  style={{ background: 'transparent', border: 'none', color: '#333', fontSize: '0.875rem', fontWeight: 700, outline: 'none', cursor: 'pointer' }} />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25 }}>
              <Box sx={{ width: 28, height: 1.5, bgcolor: '#ccc', borderRadius: 1 }} />
              {!isSingle && <Chip label={`${diffDays(startDate, endDate) + 1}d`} size="small" sx={{ bgcolor: '#e8f0fe', color: '#1a73e8', fontSize: 10, height: 16, fontWeight: 700, px: 0.25 }} />}
              {isSingle && !isRangeToday && <Chip label="past" size="small" sx={{ mt: 0.5, bgcolor: '#e8f0fe', color: '#1a73e8', fontSize: 10, height: 16, fontWeight: 700 }} />}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', border: '1.5px solid #d0d0d0', borderRadius: 2, px: 1, py: 0.75, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', minWidth: 130 }}>
              <Typography variant="caption" sx={{ color: '#999', fontWeight: 600, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.25, userSelect: 'none' }}>To</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <input type="date" value={endDate} min={startDate} onChange={onEndDateChange}
                  style={{ background: 'transparent', border: 'none', color: '#333', fontSize: '0.875rem', fontWeight: 700, outline: 'none', cursor: 'pointer' }} />
              </Box>
            </Box>

            {/* Next arrow — desktop only */}
            <Tooltip title={isSingle ? 'Next day' : 'Next period'}>
              <IconButton size="small" onClick={() => shiftRange(isSingle ? 1 : diffDays(startDate, endDate) + 1)}
                sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.secondary', border: '1.5px solid #d0d0d0', borderRadius: 1.5, p: 0.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <ChevronRight size={18} />
              </IconButton>
            </Tooltip>

            {/* Today button — desktop only */}
            {!isRangeToday && (
              <Tooltip title="Back to today">
                <IconButton size="small" onClick={goToday}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' }, color: 'text.secondary', border: '1.5px solid #d0d0d0', borderRadius: 1.5, p: 0.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <Calendar size={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Nav buttons row — mobile only */}
          <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 1 }}>
            <Tooltip title={isSingle ? 'Previous day' : 'Previous period'}>
              <IconButton size="small" onClick={() => shiftRange(-(isSingle ? 1 : diffDays(startDate, endDate) + 1))}
                sx={{ color: 'text.secondary', border: '1.5px solid #d0d0d0', borderRadius: 1.5, p: 0.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <ChevronLeft size={18} />
              </IconButton>
            </Tooltip>

            {!isRangeToday && (
              <Tooltip title="Back to today">
                <IconButton size="small" onClick={goToday}
                  sx={{ color: 'text.secondary', border: '1.5px solid #d0d0d0', borderRadius: 1.5, p: 0.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <Calendar size={16} />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title={isSingle ? 'Next day' : 'Next period'}>
              <IconButton size="small" onClick={() => shiftRange(isSingle ? 1 : diffDays(startDate, endDate) + 1)}
                sx={{ color: 'text.secondary', border: '1.5px solid #d0d0d0', borderRadius: 1.5, p: 0.5, bgcolor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <ChevronRight size={18} />
              </IconButton>
            </Tooltip>
          </Box>

        </Box>

        {/* ── Employee Cards Grid ── */}
        {activeEmployees.length > 0 ? (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 2,
            alignItems: 'start',
          }}>
            {activeEmployees.map((employee) => {
              const live = isRangeToday
                ? liveActivity?.employees?.find((e) => e.employee_id === employee.employee_id) || null
                : null;
              return (
                <EmployeeCard
                  key={employee.employee_id}
                  employee={employee}
                  isSingle={isSingle}
                  isRangeToday={isRangeToday}
                  live={live}
                  checklistData={isSingle ? (checklistData[employee.employee_id] || null) : null}
                />
              );
            })}
          </Box>
        ) : (
          <Alert severity="info">No data available for {periodLabel(startDate, endDate)}</Alert>
        )}

      </Box>

      {/* ── Rename dialog ── */}
      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold' }}>Rename Group</DialogTitle>
        <DialogContent>
          <TextField fullWidth autoFocus margin="dense" label="Group name" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} />
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setRenameOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={submitRename} disabled={renaming} variant="contained" startIcon={<Edit3 size={16} />}>
            {renaming ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Color picker dialog ── */}
      <Dialog open={colorPickerOpen} onClose={cancelColorPicker} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Palette size={20} /> Header Color
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Choose a color theme for your group header.</Typography>
          <Box style={{ background: pendingGradient }} sx={{ borderRadius: 2, p: 2, mb: 2.5, color: 'white', textAlign: 'center', fontSize: '0.9rem', fontWeight: 600, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
            {groupName} — Preview
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
            {HEADER_COLOR_PRESETS.map((preset) => (
              <Tooltip title={preset.label} key={preset.label}>
                <Box onClick={() => setPendingGradient(preset.value)} style={{ background: preset.value }}
                  sx={{ height: 48, borderRadius: 2, cursor: 'pointer', outline: pendingGradient === preset.value ? '3px solid #222' : '3px solid transparent', outlineOffset: '2px', boxShadow: pendingGradient === preset.value ? '0 0 0 1px white' : '0 1px 3px rgba(0,0,0,0.25)', transition: 'outline 0.12s, transform 0.12s', '&:hover': { transform: 'scale(1.07)' } }} />
              </Tooltip>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={cancelColorPicker} variant="outlined">Cancel</Button>
          <Button onClick={saveHeaderColor} disabled={savingColor} variant="contained" startIcon={<Palette size={16} />}>
            {savingColor ? 'Saving...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ── */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar((s) => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GroupView;